import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import * as utils from '@owl-nest/utils'

import * as entity from '@boutique/entities'

import { useProductListing } from '../hooks/useProductListing'
import { SeoLink } from './SeoLink'

type Option = { name: string; canonicalUrl?: string }

export type BooleanFilterProps = {
  data: entity.search.product.Result | undefined
  name: keyof entity.search.product.BooleanFilter
  option: Option
  title: string
  open: boolean
  onToggle: () => void
}

export const MAX_LIST_LENGTH = 15

export function BooleanFilter({ data, name, option, title, open, onToggle }: BooleanFilterProps) {
  const { unset, set, isSelected, hide } = useBooleanFilter(name, data)

  if (hide) {
    return <></>
  }

  const checkbox = (
    <CheckboxField
      onChange={() => {
        if (isSelected) {
          unset()
        } else {
          set()
        }
      }}
      checked={isSelected}
      label={option.name}
    />
  )

  return (
    <Accordion
      closedContent={
        isSelected && (
          <TagList>
            <plume.ClickableTag
              label={
                <>
                  {option.name}
                  <plume.glyphs.stroke.Close size={12} />
                </>
              }
              onClick={unset}
            />
          </TagList>
        )
      }
      title={`${title}${isSelected ? ` (1)` : ''}`}
      open={open}
      onToggle={onToggle}
    >
      {option.canonicalUrl ? <SeoLink href={option.canonicalUrl}>{checkbox}</SeoLink> : <>{checkbox}</>}
    </Accordion>
  )
}

function useBooleanFilter(
  name: keyof entity.search.product.BooleanFilter,
  data: entity.search.product.Result | undefined,
) {
  const { setFilter, apply, query } = useProductListing()

  const facet = data?.facets?.[name] ?? { data: {}, total: 0 }

  const count = facet?.data[utils.text.slugify('true', '-')] ?? 0

  return {
    unset,
    set,
    hide: count === 0,
    isSelected: query.facetFilters?.[name],
  }

  function unset() {
    apply(setFilter(name, false), { scroll: false })
  }

  function set() {
    apply(setFilter(name, true), { scroll: false })
  }
}

const Accordion = styled(plume.Accordion)`
  padding: 22px 14px 18px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    padding: 16px 14px 12px;
  }

  ${plume.glyphs.Icon} {
    position: relative;
    top: 2px;
  }

  ${plume.styles.accordion.Title} {
    margin-bottom: 0;
    padding-bottom: 0;
  }
`

const TagList = styled.div`
  margin: 16px -3px 0;

  ${plume.ClickableTag} {
    display: inline-flex;
    align-items: center;
    margin: 3px;
    color: ${plume.COLORS.PRIMARY_BLACK}; // HACK: Irregular font manipulation
  }

  ${plume.glyphs.stroke.Close} {
    margin-left: 6px;
    color: black;
    margin-top: 2px;
  }
`

const CheckboxField = styled(plume.CheckboxField)`
  padding-left: 40px;

  &:first-of-type {
    margin-top: 16px;
  }

  ${plume.styles.field.ActualLabel} {
    width: 100%;
    height: 100%;
    cursor: pointer;
    padding: 8px 0 8px 0;
  }

  &:hover {
    background: ${plume.COLORS.BLUE_SHADE_3};
  }

  ${plume.styles.input.HiddenFocusableInput} {
    margin: 8px 0 8px 8px;
  }

  ${plume.ControlledCheckboxWrapper} {
    margin: 8px 0 8px 8px;
    top: 0;
  }

  ${plume.styles.field.Title} {
    ${plume.styles.copy._smallStyle}
    margin-top: -2px;
  }
`
